/**
 * Enviar una factura por email, con la dirección del cliente ya puesta.
 *
 * POR QUÉ EXISTE
 * --------------
 * Angel, 7-sep-2026: hay que reenviar 15 facturas corregidas, y el botón del
 * sobre hacía esto:
 *
 *     const email = prompt('Introduce el email del cliente:');
 *
 * Es decir: teclear la dirección a mano quince veces, sin ver de qué factura
 * se trata ni a quién va. Quince oportunidades de escribirla mal, y un error
 * ahí no da error: la factura "se envía" a una dirección que no existe.
 *
 * Ahora viene puesta la de la ficha y se puede corregir antes de enviar.
 *
 * El campo admite VARIAS separadas por coma (el cliente y su gestoría). El
 * servidor las separa en `destinatarios()`; aquí solo se enseñan para que
 * quien envía vea a cuántos va.
 */
import React, { useEffect, useState } from 'react';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { mensajeDeError } from '@/lib/errores';
import { direccionesDe } from '@/lib/destinatarios';
import { estadoDeEnvio } from '@/lib/envio';

export function DialogoEnviarFactura({ invoice, open, onOpenChange, onEnviado }) {
  const [email, setEmail] = useState('');
  const [cargando, setCargando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [cliente, setCliente] = useState(null);

  useEffect(() => {
    if (!open || !invoice) return;
    let vigente = true;
    setCargando(true);
    setCliente(null);
    (async () => {
      try {
        const { data } = await axiosInstance.get(`/customers/${invoice.customer_id}`);
        if (!vigente) return;
        setCliente(data);
        // La de facturación primero: es la que el cliente dio PARA esto. La
        // general es el contacto comercial y no siempre es la misma.
        setEmail(data?.billing_email || data?.email || '');
      } catch (e) {
        if (vigente) setEmail('');
      } finally {
        if (vigente) setCargando(false);
      }
    })();
    return () => { vigente = false; };
  }, [open, invoice]);

  const destinos = direccionesDe(email);
  // Si ya le llego, se dice ANTES del boton. Angel, 15-sep-2026: "para no
  // enviarlas dos veces". No se bloquea: reenviar es legitimo (una corregida,
  // una que el cliente dice que no ve). Pero que sea a sabiendas.
  const yaEnviada = estadoDeEnvio(invoice);

  const enviar = async () => {
    setEnviando(true);
    try {
      const { data } = await axiosInstance.post(
        `/invoices/${invoice.id}/send-email`, { to_email: email.trim(), message: '' });
      if (data.success) {
        toast.success(data.message || 'Factura enviada');
        onOpenChange(false);
        onEnviado?.(data);
      } else {
        // success:false es un fallo de verdad, no un aviso. Desde abril el
        // envio dice si salio o no, y aqui hay que creerselo.
        toast.error(data.message || 'No se pudo enviar');
      }
    } catch (e) {
      toast.error(mensajeDeError(e));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="dialogo-enviar-factura">
        <DialogHeader>
          <DialogTitle>Enviar {invoice?.invoice_number}</DialogTitle>
          <DialogDescription>
            {invoice?.customer_name} · {(invoice?.total ?? 0).toFixed(2)} €
          </DialogDescription>
        </DialogHeader>

        {yaEnviada.enviada && (
          <div className="border border-amber-200 bg-amber-50 rounded p-3"
               data-testid="aviso-ya-enviada">
            <p className="text-amber-900 text-sm flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                <strong>Esta factura ya se envió.</strong> {yaEnviada.titulo}.
                Si la envías otra vez, el cliente la recibirá dos veces.
              </span>
            </p>
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="destino">Enviar a</Label>
          <Input
            id="destino" type="text" value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={cargando ? 'Cargando la ficha...' : 'cliente@ejemplo.es'}
            disabled={cargando}
            data-testid="destino-factura"
          />
          {!cargando && destinos.length > 1 && (
            <p className="text-xs text-slate-500">
              Va a {destinos.length} direcciones: {destinos.join(', ')}
            </p>
          )}
          {!cargando && !destinos.length && (
            <p className="text-xs text-amber-700 flex items-start gap-1">
              <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
              {cliente
                ? 'La ficha de este cliente no tiene email de facturación. Escríbelo aquí y ponlo también en la ficha.'
                : 'No se pudo leer la ficha del cliente. Escribe la dirección a mano.'}
            </p>
          )}
          <p className="text-xs text-slate-500">
            Varias direcciones separadas por coma llegan a todas.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={enviar} disabled={!destinos.length || enviando || cargando}
                  className="bg-[#9C0F17] hover:bg-[#7A0C12] text-white"
                  data-testid="confirmar-envio-factura">
            {enviando
              ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enviando...</>)
              : `${yaEnviada.enviada ? 'Enviar otra vez' : 'Enviar'}${destinos.length > 1 ? ` a ${destinos.length}` : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DialogoEnviarFactura;
